<template>
  <v-form @submit.prevent="validate()" ref="form">
    <v-snackbar v-model="snackbar.visibility" :timeout="snackbar.timeout" :color="snackbar.color">
      {{ snackbar.text }}
      <v-btn text @click="remove()" v-show="snackbar.confirm">Confirm</v-btn>
      <v-btn text @click="setSnackbar({ ...snackbar, visibility: false })">{{ snackbar.cancelText }}</v-btn>
    </v-snackbar>
    <v-container grid-list-md>
      <v-row dense>
        <v-col cols="12" md="3">
          <gallery-input :max="10" :gallery.sync="location.gallery" />
        </v-col>
        <v-col cols="12" md="9">
          <v-row>
            <v-col cols="12" class="py-0">
              <v-text-field v-model="location.name" label="Name" :rules="[rules.required]" />
            </v-col>
          </v-row>
          <v-row>
            <v-col cols="12" md="6" class="py-0">
              <v-text-field v-model="location.code" label="Code" />
            </v-col>
            <v-col cols="12" md="6" class="py-0">
              <v-select v-model="location.campus" :items="campuses" item-text="name" item-value="_id" label="Campus" append-outer-icon="fa-plus" @click:append-outer="newCampus()" :rules="[rules.required]" />
            </v-col>
          </v-row>
          <v-row>
            <v-col cols="12" md="6" class="py-0">
              <v-select v-model="location.category" :items="categories" item-text="name" item-value="_id" label="Category" append-outer-icon="fa-plus" @click:append-outer="newCategory()" :rules="[rules.required]" @change="onCategoryChange()" return-object />
            </v-col>
            <v-col cols="12" md="6" class="py-0">
              <v-combobox v-if="location.category" v-model="location.subcategories" :items="subcategories" label="Subcategories" multiple chips deletable-chips />
            </v-col>
          </v-row>
          <v-row>
            <v-col class="py-0">
              <map-input :coordinates.sync="coordinates" :rules="[rules.required]" />
            </v-col>
          </v-row>
          <v-row>
            <v-col cols="12" class="py-0">
              <v-textarea v-model="location.description" label="Description" outlined />
            </v-col>
          </v-row>
          <v-row>
            <v-col cols="12" class="py-0">
              <v-textarea v-model="location.covidInfo" label="COVID-19 Information" outlined />
            </v-col>
          </v-row>
          <v-row>
            <v-col cols="12" class="py-0">
              <v-combobox
                v-model="location.tags"
                :items="tags"
                chips
                label="Tags"
                multiple
                solo
                deletable-chips
              />
            </v-col>
          </v-row>
          <v-row>
            <v-col cols="12" class="py-0">
              <v-radio-group v-model="location.accessibility" row label="Accessibility" mandatory>
                <v-radio label="Unknown" value="" color="primary"  />
                <v-radio label="Full" value="full" color="primary" />
                <v-radio label="Partial" value="partial" color="primary" />
                <v-radio label="None" value="none" color="primary" />
              </v-radio-group>
            </v-col>
          </v-row>
        </v-col>
      </v-row>
      <v-row dense>
        <v-col cols="12">
          <v-expansion-panels>
            <v-expansion-panel v-if="location.hours">
              <v-expansion-panel-header>Hours</v-expansion-panel-header>
              <v-expansion-panel-content>
                <v-container grid-list-md>
                  <v-layout wrap>
                    <v-flex
                      v-for="(timeslots, index) in Object.entries(timePickers)"
                      :key="`timePickers-${index}`"
                      xs12
                      md3>
                      <fieldset v-if="location.hours[timeslots[0]]">
                        <legend>{{ timeslots[0].charAt(0).toUpperCase() + timeslots[0].slice(1) }}</legend>

                        <v-dialog
                          v-for="(timeslot, timeslotIndex) in Object.entries(timeslots[1])"
                          :key="`timeslot-${timeslotIndex}`"
                          :ref="`dialog-${timeslots[0]}-${timeslot[0]}`"
                          v-model="timePickers[timeslots[0]][timeslot[0]]"
                          :return-value.sync="location.hours[timeslots[0]][timeslot[0]]"
                          width="290px">
                          <template v-slot:activator="{ on, attrs }">
                            <v-text-field
                              v-model="location.hours[timeslots[0]][timeslot[0]]"
                              :label="timeslot[0].charAt(0).toUpperCase() + timeslot[0].slice(1)"
                              readonly
                              v-bind="attrs"
                              v-on="on"
                              clearable />
                          </template>
                          <v-time-picker
                            v-if="$refs[`dialog-${timeslots[0]}-${timeslot[0]}`]"
                            v-model="location.hours[timeslots[0]][timeslot[0]]"
                            full-width>
                            <v-spacer />
                            <v-btn text color="primary" @click="timePickers[timeslots[0]][timeslot[0]] = false">Cancel</v-btn>
                            <v-btn
                              text
                              color="primary"
                              @click="$refs[`dialog-${timeslots[0]}-${timeslot[0]}`][0].save(location.hours[timeslots[0]][timeslot[0]])">
                              OK
                            </v-btn>
                          </v-time-picker>
                        </v-dialog>
                      </fieldset>
                    </v-flex>
                  </v-layout>
                </v-container>
              </v-expansion-panel-content>
            </v-expansion-panel>
            <v-expansion-panel v-if="location.alert">
              <v-expansion-panel-header>Alert</v-expansion-panel-header>
              <v-expansion-panel-content>
                <v-container grid-list-md>
                  <v-layout wrap>
                    <v-flex xs6>
                      <v-datetime-picker label="Start" v-model="alert.start">
                        <template slot="dateIcon">
                          <v-icon>fa-calendar</v-icon>
                        </template>
                        <template slot="timeIcon">
                          <v-icon>fa-clock</v-icon>
                        </template>
                      </v-datetime-picker>
                    </v-flex>
                    <v-flex xs6>
                      <v-datetime-picker label="End" v-model="alert.end">
                        <template slot="dateIcon">
                          <v-icon>fa-calendar</v-icon>
                        </template>
                        <template slot="timeIcon">
                          <v-icon>fa-clock</v-icon>
                        </template>
                      </v-datetime-picker>
                    </v-flex>
                    <v-flex xs12>
                      <v-textarea v-model="location.alert.text" label="Text" outlined />
                    </v-flex>
                  </v-layout>
                </v-container>
              </v-expansion-panel-content>
            </v-expansion-panel>
            <v-expansion-panel v-if="location.tour">
              <v-expansion-panel-header>Tours</v-expansion-panel-header>
              <v-expansion-panel-content>
                <v-container grid-list-md>
                  <v-layout wrap>
                    <v-flex xs12>
                      <v-text-field v-model="location.tour.desktop" label="Desktop" />
                      <v-text-field v-model="location.tour.mobile" label="Mobile" />
                    </v-flex>
                  </v-layout>
                </v-container>
              </v-expansion-panel-content>
            </v-expansion-panel>
            <v-expansion-panel v-if="location.emergencyPhones">
              <v-expansion-panel-header>Emergency Telephones</v-expansion-panel-header>
              <v-expansion-panel-content>
                <v-list>
                  <v-list-item v-for="(phone, index) in location.emergencyPhones" :key="index">
                    <v-list-item-content>
                      <v-row class="py-0">
                        <v-col cols="6" class="py-0">
                          <v-text-field
                            v-model="phone.name"
                            label="Name"
                            prepend-icon="fa-minus"
                            @click:prepend="location.emergencyPhones.splice(index, 1)"
                          />
                        </v-col>
                        <v-col cols="6" class="py-0">
                          <map-input :coordinates.sync="phone.coordinates" />
                        </v-col>
                      </v-row>
                    </v-list-item-content>
                  </v-list-item>
                  <v-divider v-if="location.emergencyPhones.length" />
                  <v-list-item>
                    <v-list-item-content>
                      <v-row>
                        <v-col cols="6" class="py-0">
                          <v-text-field
                            v-model="newEmergencyPhone.name"
                            label="Name"
                            prepend-icon="fa-plus"
                            @click:prepend="addEmergencyPhone()"
                          />
                        </v-col>
                        <v-col cols="6" class="py-0">
                          <map-input :coordinates.sync="newEmergencyPhone.coordinates" />
                        </v-col>
                      </v-row>
                    </v-list-item-content>
                  </v-list-item>
                </v-list>
              </v-expansion-panel-content>
            </v-expansion-panel>
            <v-expansion-panel v-if="location.videos">
              <v-expansion-panel-header>Videos</v-expansion-panel-header>
              <v-expansion-panel-content>
                <v-list>
                  <v-list-item v-for="(video, index) in location.videos" :key="index">
                    <v-list-item-content>
                      <v-row class="py-0">
                        <v-col cols="12" class="py-0">
                          <v-text-field
                            v-model="location.videos[index]"
                            label="URL"
                            placeholder="https://www.youtube.com/watch?v=djV11Xbc914"
                            prepend-icon="fa-minus"
                            @click:prepend="location.videos.splice(index, 1)"
                          />
                        </v-col>
                      </v-row>
                    </v-list-item-content>
                  </v-list-item>
                  <v-divider v-if="location.videos.length" />
                  <v-list-item>
                    <v-list-item-content>
                      <v-row>
                        <v-col cols="12" class="py-0">
                          <v-text-field
                            v-model="newVideo"
                            label="URL"
                            placeholder="https://www.youtube.com/watch?v=djV11Xbc914"
                            prepend-icon="fa-plus"
                            @click:prepend="addVideo()"
                          />
                        </v-col>
                      </v-row>
                    </v-list-item-content>
                  </v-list-item>
                </v-list>
              </v-expansion-panel-content>
            </v-expansion-panel>
          </v-expansion-panels>
        </v-col>
      </v-row>
      <v-row>
        <v-col cols="12">
          <v-btn type="submit" block color="primary">Submit</v-btn>
        </v-col>
      </v-row>
    </v-container>
  </v-form>
</template>

<script>
import { mapGetters, mapMutations } from 'vuex'

import LocationsService from '@/services/LocationsService'
import rules from '@/mixins/rules'

export default {
  name: 'LocationForm',
  mixins: [rules],
  data () {
    return {
      valid: false,
      alert: {
        start: null,
        end: null
      },
      coordinates: [0, 0],
      newEmergencyPhone: {},
      newVideo: '',
      timePickers: {
        sunday: {
          open: false,
          close: false
        },
        monday: {
          open: false,
          close: false
        },
        tuesday: {
          open: false,
          close: false
        },
        wednesday: {
          open: false,
          close: false
        },
        thursday: {
          open: false,
          close: false
        },
        friday: {
          open: false,
          close: false
        },
        saturday: {
          open: false,
          close: false
        }
      },
      location: {}
    }
  },
  computed: {
    ...mapGetters([
      'categories',
      'campuses',
      'locations',
      'selectedLocation',
      'snackbar',
      'tags'
    ]),
    subcategories () {
      if (this.location.category) {
        const tempCategories = this.categories.filter(category => category._id === this.location.category._id)

        return (tempCategories.length) ? tempCategories[0].subcategories : []
      } else {
        return []
      }
    }
  },
  watch: {
    coordinates () {
      this.location.lat = this.coordinates[0]
      this.location.lng = this.coordinates[1]
    },
    location: {
      handler: 'onLocationChange'
    },
    selectedLocation () {
      this.buildLocation()
    },
    alert: {
      deep: true,
      handler () {
        if (this.alert.start && typeof this.alert.start.toISOString === 'function') {
          this.location.alert.start = this.alert.start.toISOString()
        } else {
          this.location.alert.start = null
        }

        if (this.alert.end && typeof this.alert.start.toISOString === 'function') {
          this.location.alert.end = this.alert.end.toISOString()
        } else {
          this.location.alert.end = null
        }
      }
    }
  },
  mounted () {
    this.onLocationChange()
    this.buildLocation()
  },
  methods: {
    ...mapMutations([
      'setSnackbar'
    ]),
    async validate () {
      if (this.$refs.form.validate()) {
        try {
          let response
          if (this.$route.params.id) {
            response = await LocationsService.updateLocation(this.location)
          } else {
            response = await LocationsService.addLocation(this.location)
          }

          this.setSnackbar({
            ...this.snackbar,
            text: response.data.message,
            color: 'success',
            cancelText: 'Close',
            confirm: false,
            visibility: true
          })

          this.$router.push('/admin/locations')
        } catch (e) {
          if (e.response.status === 403) {
            this.$router.push('/login')
          }

          this.setSnackbar({
            ...this.snackbar,
            text: 'Unable to modify location',
            color: 'error',
            cancelText: 'Close',
            confirm: false,
            visibility: true
          })

          console.error(e)
        }
      }
    },
    buildLocation () {
      this.location = Object.assign({}, this.selectedLocation || {})

      if (!this.location.hours) this.location.hours = {}
      if (!this.location.hours.sunday) this.location.hours.sunday = {}
      if (!this.location.hours.monday) this.location.hours.monday = {}
      if (!this.location.hours.tuesday) this.location.hours.tuesday = {}
      if (!this.location.hours.wednesday) this.location.hours.wednesday = {}
      if (!this.location.hours.thursday) this.location.hours.thursday = {}
      if (!this.location.hours.friday) this.location.hours.friday = {}
      if (!this.location.hours.saturday) this.location.hours.saturday = {}

      if (!this.location.alert) this.location.alert = {}
      else {
        if (this.location.alert.start) this.alert.start = new Date(this.location.alert.start)
        if (this.location.alert.end) this.alert.end = new Date(this.location.alert.end)
      }

      if (!this.location.tour) this.location.tour = {}
      if (!this.location.emergencyPhones) this.location.emergencyPhones = []
      if (!this.location.videos) this.location.videos = []

      if (!this.location.accessibility) this.location.accessibility = ''
    },
    newCategory () {
      this.$router.push('/admin/categories/new')
    },
    newCampus () {
      this.$router.push('/admin/campuses/new')
    },
    addEmergencyPhone () {
      this.location.emergencyPhones.push(this.newEmergencyPhone)
      this.newEmergencyPhone = {}
    },
    addVideo () {
      this.location.videos.push(this.newVideo)
      this.newVideo = ''
    },
    onLocationChange () {
      if (this.location.lat && this.location.lng) {
        this.coordinates = [this.location.lat, this.location.lng]
      }
    },
    onCategoryChange () {
      this.location.subcategories = []
    },
    resetAlert (time) {
      if (time !== 'start' && time !== 'end') return

      this.alert[time].date = null
      this.alert[time].time = null
    }
  }
}
</script>

<style lang="scss" scoped>
.v-input {
  margin-top: 0;
}

.v-textarea {
  label {
    width: auto;
  }
}

fieldset {
  border-radius: 4px;
  padding: 9px 16px;
}
</style>
